import {
  type LocalPublicationReceipt,
  type PublicationCompanion,
  PublicationError,
  type PublicationKind,
  type PublicationMemberBase,
} from '@streamlain/doc-publication-contract';
import type {
  DatabaseImportPort,
  DatabaseRowTransactionPort,
} from '@streamlain/doc-publication-contract/database-writer';
import {
  applyUpdate,
  decodeStateVector,
  Doc,
  encodeStateAsUpdate,
  encodeStateVector,
} from 'yjs';

import type {
  DatabasePublicationPort,
  DatabasePublicationSyncPort,
  DatabaseRelationInversePlanner,
} from './data-source-contracts.js';

/** All stage docs are isolated, use durable actors, and are destroyed after ACK/failure. */
export class DatabasePublicationStage {
  private readonly docs = new Map<string, Doc>();
  private readonly vectors = new Map<string, Uint8Array>();
  private readonly actors = new Map<string, number>();
  private readonly bases = new Map<string, PublicationMemberBase>();
  private readonly occupied = new Set<number>();
  /**
   * Member updates exactly as the publication committed them. Stores bound to
   * the stage stay alive until it is destroyed and may write into its docs
   * after the commit; those writes carry the durable actor but never reached
   * its ledger, so they must not reach a live doc either.
   */
  private committed: ReadonlyMap<string, Uint8Array> | undefined;
  /** Stage docs a write changed after their base was loaded. */
  private readonly changedDocs = new Set<string>();

  constructor(
    bases: readonly PublicationMemberBase[],
    liveClientIds: readonly number[]
  ) {
    const occupied = this.occupied;
    liveClientIds.forEach(id => occupied.add(id));
    try {
      for (const base of bases) {
        const doc = new Doc({ guid: base.docId });
        this.docs.set(base.docId, doc);
        this.bases.set(base.docId, base);
        try {
          if (base.bin) applyUpdate(doc, base.bin);
        } catch {
          // The outer cleanup destroys every stage doc, including this member.
          throw new PublicationError('FORMAT_MISMATCH');
        }
        doc.on('update', () => this.changedDocs.add(base.docId));
        const vector = encodeStateVector(doc);
        this.vectors.set(base.docId, vector);
        for (const id of decodeStateVector(vector).keys()) occupied.add(id);
        if (base.actor) occupied.add(base.actor.clientId);
      }
      for (const base of bases) {
        const doc = this.doc(base.docId);
        if (base.actor) {
          if (
            liveClientIds.includes(base.actor.clientId) ||
            (decodeStateVector(this.vectors.get(base.docId)!).get(
              base.actor.clientId
            ) ?? 0) !== base.actor.nextClock
          )
            throw new PublicationError('FORMAT_MISMATCH');
          doc.clientID = base.actor.clientId;
        } else {
          while (occupied.has(doc.clientID)) {
            doc.clientID = randomClientId();
          }
          occupied.add(doc.clientID);
        }
        this.actors.set(base.docId, doc.clientID);
      }
    } catch (error) {
      this.destroy();
      throw error;
    }
  }

  addEmptyDoc(id: string): Doc {
    if (this.docs.has(id)) return this.doc(id);
    const doc = new Doc({ guid: id });
    while (this.occupied.has(doc.clientID)) {
      doc.clientID = randomClientId();
    }
    this.occupied.add(doc.clientID);
    doc.on('update', () => this.changedDocs.add(id));
    this.docs.set(id, doc);
    this.vectors.set(id, encodeStateVector(doc));
    this.actors.set(id, doc.clientID);
    this.bases.set(id, { docId: id, bin: null, clock: null, actor: null });
    return doc;
  }

  /** Whether a write changed any stage doc; an unchanged stage has nothing to publish. */
  changed(): boolean {
    return this.changedDocs.size > 0;
  }

  ids(): readonly string[] {
    return [...this.docs.keys()];
  }

  doc(id: string): Doc {
    const doc = this.docs.get(id);
    if (!doc) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
    return doc;
  }

  base(id: string): PublicationMemberBase {
    const base = this.bases.get(id);
    if (!base) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
    return base;
  }

  /**
   * Commits the stage docs as members; `companions` are live-doc changes the
   * same send carries, such as the page block that hosts a new database.
   */
  async commit(
    port: DatabasePublicationPort,
    workspaceId: string,
    kind: PublicationKind,
    companions: readonly PublicationCompanion[],
    signal?: AbortSignal
  ): Promise<LocalPublicationReceipt> {
    if (signal?.aborted) throw new PublicationError('CANCELLED');
    const lengths = [...this.docs.values()].map(
      doc => encodeStateAsUpdate(doc).byteLength
    );
    if (
      lengths.length > 64 ||
      lengths.some(length => length > 10 * 1024 * 1024) ||
      lengths.reduce((sum, length) => sum + length, 0) > 40 * 1024 * 1024
    )
      throw new PublicationError('LIMIT_EXCEEDED');
    const members = [...this.docs].map(([docId, doc]) => {
      const base = this.base(docId);
      const clientId = this.actors.get(docId)!;
      return {
        docId,
        update: encodeStateAsUpdate(doc, this.vectors.get(docId)),
        expectedClock: base.clock,
        expectedActor: base.actor,
        nextActor: {
          clientId,
          nextClock:
            decodeStateVector(encodeStateVector(doc)).get(clientId) ?? 0,
        },
      };
    });
    const receipt = await port.commitPublication(
      {
        operationId: crypto.randomUUID(),
        workspaceId,
        members,
        companions,
        kind,
      },
      signal
    );
    this.committed = new Map(
      members.map(member => [member.docId, member.update])
    );
    return receipt;
  }

  /**
   * Update of `id` since its base; after the commit exactly the committed
   * bytes, so a live doc never receives an actor clock its ledger lacks.
   */
  update(id: string): Uint8Array {
    if (!this.committed)
      return encodeStateAsUpdate(this.doc(id), this.vectors.get(id));
    const update = this.committed.get(id);
    if (!update) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
    return update;
  }

  destroy(): void {
    for (const doc of this.docs.values()) doc.destroy();
    this.docs.clear();
  }
}

export interface DatabaseWriterPorts {
  readonly rows: DatabaseRowTransactionPort;
  readonly importer?: DatabaseImportPort;
  readonly publication: DatabasePublicationPort;
  readonly sync: DatabasePublicationSyncPort;
  readonly inverse: DatabaseRelationInversePlanner;
}
const writerPorts = new WeakMap<object, DatabaseWriterPorts>();
export function registerDatabaseWriterPorts(
  workspace: object,
  ports: DatabaseWriterPorts
): () => void {
  if (writerPorts.has(workspace)) throw new PublicationError('FORMAT_MISMATCH');
  writerPorts.set(workspace, ports);
  return () => {
    if (writerPorts.get(workspace) === ports) writerPorts.delete(workspace);
  };
}
export function getDatabaseWriterPorts(workspace: object): DatabaseWriterPorts {
  const ports = writerPorts.get(workspace);
  if (!ports) throw new PublicationError('FORMAT_MISMATCH');
  return ports;
}

/** Creation callbacks edit isolated stores; their completion is the outer receipt. */
export interface DatabaseCreationStageBinding {
  readonly stage: DatabasePublicationStage;
  readonly receipt: Promise<LocalPublicationReceipt | null>;
}
const creationStages = new WeakMap<object, DatabaseCreationStageBinding>();
export function bindDatabaseCreationStage(
  store: object,
  binding: DatabaseCreationStageBinding
): () => void {
  creationStages.set(store, binding);
  return () => creationStages.delete(store);
}
export function getDatabaseCreationStage(
  store: object
): DatabaseCreationStageBinding | undefined {
  return creationStages.get(store);
}

/** A random 53-bit Yjs client id, the range Y.Doc itself draws from. */
function randomClientId(): number {
  const [high = 0, low = 0] = crypto.getRandomValues(new Uint32Array(2));
  return (high & 0x1fffff) * 0x100000000 + low;
}
